// Core
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// Libraries
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import dayjs, { Dayjs } from 'dayjs';

// Plugins
import { requestVantageApi, RequestMethod } from '../../../../../../plugins/request';

// Store
import { getCustomer, getUser } from '../../../../../../store/session.store';
import { getMeasurementUnit } from '../../../../../../store/measurement.store';

// Types
import { Theme } from '../../../../../../types/theme';
import {
  AsyncTaskStatus,
  AsyncTaskType,
  Customer,
  LengthType,
  MeasurementType,
  PressureType,
  TemperatureType,
  User,
  UserType,
} from '../../../../../../types/global';
import {
  ExportPeriodPreset,
  ExportReportType,
} from '../../../../../../types/crusher';

// Components
import { Alert, AlertType } from '../../../../../../layout/elements/Alert';
import { DatePicker, DatePickerType } from '../../../../../../layout/elements/DatePicker';
import { Drawer, DrawerAction, DrawerSize } from '../../../../../../layout/elements/Drawer';
import { Loader, LoaderSize } from '../../../../../../layout/elements/Loader';
import {
  OptionsSwitch,
  OptionsSwitchProps,
} from '../../../../../../layout/elements/OptionsSwitch';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../layout/elements/Paragraph';
import { Select } from '../../../../../../layout/elements/Select';
import { AlertLine } from '../../../../../../layout/icons';
import { showToast, ToastType } from '../../../../../../layout/elements/Toast';

// Component
import {
  DatePickerWrapper,
  ExportContent,
  HelperText,
  PeriodSelectorContainer,
  PresetsWrapper,
  SectionContainer,
  SectionContent,
  TaskStatusContainer,
} from './Export.style';
import {
  ExportCreateTaskBody,
  ExportProps,
  ExportTask,
} from './Export.types';

// Functional constants
const POLLING_INTERVAL_MS: number = 2000;

const triggerFileDownload = (url: string): void => {
  const anchor: HTMLAnchorElement = document.createElement('a');
  anchor.href = url;
  anchor.download = '';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

function Export({
  className,
  equipmentUnit,
  isDrawerOpen,
  onCloseDrawer,
}: ExportProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const theme: Theme = useTheme();

  // User and customer context
  const user: User | null = getUser();
  const customer: Customer | null = getCustomer();
  const isAdmin: boolean = user?.type === UserType.admin;

  /* ***********************************************************************************************
  *************************************** INITIAL STATE ********************************************
  *********************************************************************************************** */

  type State = {
    periodPreset: ExportPeriodPreset | null;
    startDate: Date | null;
    endDate: Date | null;
    isCreatingTask: boolean;
    currentTask: ExportTask | null;
    temperatureUnit: TemperatureType;
    pressureUnit: PressureType;
    lengthUnit: LengthType;
  };

  const initialState: State = {
    periodPreset: null,
    startDate: null,
    endDate: null,
    isCreatingTask: false,
    currentTask: null,
    temperatureUnit: getMeasurementUnit(MeasurementType.temperature),
    pressureUnit: getMeasurementUnit(MeasurementType.pressure),
    lengthUnit: getMeasurementUnit(MeasurementType.length),
  };

  /* ***********************************************************************************************
  *************************************** LOCAL STATE **********************************************
  *********************************************************************************************** */

  const [state, setState] = useState<State>(initialState);

  const currentTaskRef = useRef<ExportTask | null>(state.currentTask);

  /* ***********************************************************************************************
  ****************************************** METHODS ***********************************************
  *********************************************************************************************** */

  // Fetch task status (used by polling)
  const fetchTaskStatus = useCallback(async (): Promise<void> => {
    const task: ExportTask | null = currentTaskRef.current;
    if (!task) return;

    // Do not poll if status is terminal
    if (
      task.status === AsyncTaskStatus.completed
      || task.status === AsyncTaskStatus.failed
    ) return;

    try {
      const params: Record<string, string> = {};
      if (user?.type === UserType.admin && customer) {
        params.idCustomer = customer.id;
      }

      const response: ExportTask = await requestVantageApi.asyncTask({
        method: RequestMethod.GET,
        path: `/async-task/${task.idAsyncTask}`,
        params,
        handleErrors: false,
      });

      setState((prev: State): State => ({
        ...prev,
        currentTask: response,
      }));

      // Auto-download when completed (skip if no data was exported)
      if (
        response.status === AsyncTaskStatus.completed
        && response.metadata?.result?.downloadUrl
        && !response.metadata?.result?.noData
      ) {
        triggerFileDownload(response.metadata.result.downloadUrl);
      }
    } catch (error: any) {
      showToast({
        type: ToastType.error,
        text: t('common.defaultError'),
      });

      setState((prev: State): State => ({
        ...prev,
        currentTask: prev.currentTask
          ? {
            ...prev.currentTask,
            status: AsyncTaskStatus.failed,
            metadata: {
              ...prev.currentTask.metadata,
              error: { message: String(t('common.defaultError')) },
            },
          }
          : null,
      }));
    }
  }, [
    user,
    customer,
    t,
    currentTaskRef,
  ]);

  // Create async task
  const handleCreateTask = async (): Promise<void> => {
    if (!state.startDate || !state.endDate) return;

    setState((prev: State): State => ({
      ...prev,
      isCreatingTask: true,
    }));

    try {
      const params: Record<string, string> = {};
      if (user?.type === UserType.admin && customer) {
        params.idCustomer = customer.id;
      }

      const body: ExportCreateTaskBody = {
        task: AsyncTaskType.export_crusher_main_dashboard,
        idResource: equipmentUnit.id,
        metadata: {
          filters: {
            idEquipmentUnit: equipmentUnit.id,
            startDatetime: state.startDate.toISOString(),
            stopDatetime: state.endDate.toISOString(),
            temperatureUnit: state.temperatureUnit,
            pressureUnit: state.pressureUnit,
            lengthUnit: state.lengthUnit,
          },
        },
      };

      const response: { idAsyncTask: string; status: string } = await requestVantageApi.asyncTask({
        method: RequestMethod.POST,
        path: '/async-task',
        params,
        body,
        handleErrors: false,
      });

      setState((prev: State): State => ({
        ...prev,
        isCreatingTask: false,
        currentTask: {
          idAsyncTask: response.idAsyncTask,
          task: AsyncTaskType.export_crusher_main_dashboard,
          status: response.status as AsyncTaskStatus,
          metadata: {},
          createdAt: new Date().toISOString(),
        },
      }));
    } catch (error: any) {
      if (
        error.response?.status === 422
        && error.response?.data
          === 'Export period cannot exceed 7 days. Please reduce the date range between startDatetime and stopDatetime.'
      ) {
        showToast({
          type: ToastType.error,
          text: t('view.crusher.export.periodExceededError'),
        });
      } else {
        showToast({
          type: ToastType.error,
          text: t('common.defaultError'),
        });
      }

      setState((prev: State): State => ({
        ...prev,
        isCreatingTask: false,
      }));
    }
  };

  const handleRetry = (): void => {
    setState((prev: State): State => ({
      ...prev,
      currentTask: null,
    }));
  };

  // Check if task is in active state (pending or processing)
  const isTaskActive: boolean = (
    !!state.currentTask
    && state.currentTask.status !== AsyncTaskStatus.completed
    && state.currentTask.status !== AsyncTaskStatus.failed
  );

  // Check if export button should be disabled
  const isExportDisabled: boolean = (
    !state.startDate
    || !state.endDate
    || state.isCreatingTask
    || isTaskActive
  );

  // Determine primary action based on task status
  const getPrimaryAction = (): DrawerAction | undefined => {
    if (!isAdmin) {
      return undefined;
    }
    if (
      state.currentTask?.status === AsyncTaskStatus.completed
      && !state.currentTask?.metadata?.result?.noData
    ) {
      return {
        children: String(t('view.crusher.export.downloadButton')),
        onClick: (): void => {
          const url = state.currentTask?.metadata?.result?.downloadUrl;
          if (url) triggerFileDownload(url);
        },
      };
    }

    if (
      state.currentTask?.status === AsyncTaskStatus.completed
      && state.currentTask?.metadata?.result?.noData
    ) {
      return {
        children: String(t('view.crusher.export.tryAgain')),
        onClick: handleRetry,
      };
    }

    if (state.currentTask?.status === AsyncTaskStatus.failed) {
      return {
        children: String(t('view.crusher.export.tryAgain')),
        onClick: handleRetry,
      };
    }

    return {
      children: String(t('view.crusher.export.exportButton')),
      onClick: handleCreateTask,
      disabled: isExportDisabled,
    };
  };

  // Period preset options (i18n)
  const periodPresetOptions: { label: string; value: string }[] = useMemo(
    (): { label: string; value: string }[] => [
      { label: t('view.crusher.export.period1d'), value: ExportPeriodPreset.oneDay },
      { label: t('view.crusher.export.period3d'), value: ExportPeriodPreset.threeDays },
      { label: t('view.crusher.export.period7d'), value: ExportPeriodPreset.sevenDays },
    ],
    [t],
  );

  const handlePresetSelection = useCallback((preset: ExportPeriodPreset): void => {
    const now: Dayjs = dayjs();
    let start: Date;
    const end: Date = now.toDate();
    switch (preset) {
      case ExportPeriodPreset.oneDay:
        start = now.subtract(1, 'day').toDate();
        break;
      case ExportPeriodPreset.threeDays:
        start = now.subtract(3, 'days').toDate();
        break;
      case ExportPeriodPreset.sevenDays:
        start = now.subtract(7, 'days').toDate();
        break;
      default:
        start = now.subtract(1, 'day').toDate();
    }
    setState((prev: State): State => ({
      ...prev,
      periodPreset: preset,
      startDate: start,
      endDate: end,
      currentTask: null,
    }));
  }, []);

  /* ***********************************************************************************************
  *************************************** COMPONENT HANDLING ***************************************
  *********************************************************************************************** */

  useEffect((): void => {
    currentTaskRef.current = state.currentTask;
  }, [state.currentTask]);

  // Auto-select 1d preset when drawer opens; reset state only when opening fresh (no active task)
  useEffect((): void => {
    if (isDrawerOpen && !state.startDate) {
      handlePresetSelection(ExportPeriodPreset.oneDay);
    }

    if (isDrawerOpen) {
      const task: ExportTask | null = currentTaskRef.current;
      const isTerminal: boolean = (
        !task
        || task.status === AsyncTaskStatus.completed
        || task.status === AsyncTaskStatus.failed
      );

      if (isTerminal) {
        setState((prev: State): State => ({
          ...prev,
          currentTask: null,
        }));
      }
    }
  }, [isDrawerOpen, state.startDate, handlePresetSelection]);

  // Poll task status while active, triggering auto-download on completion
  useEffect((): (() => void) | undefined => {
    const taskId: string | undefined = state.currentTask?.idAsyncTask;
    const taskStatus: AsyncTaskStatus | undefined = state.currentTask?.status;

    if (
      !taskId
      || taskStatus === AsyncTaskStatus.completed
      || taskStatus === AsyncTaskStatus.failed
    ) return undefined;

    fetchTaskStatus();

    const intervalId: NodeJS.Timeout = setInterval((): void => {
      fetchTaskStatus();
    }, POLLING_INTERVAL_MS);

    return (): void => {
      clearInterval(intervalId);
    };
  }, [
    fetchTaskStatus,
    state.currentTask?.idAsyncTask,
    state.currentTask?.status,
  ]);

  return (
    <Drawer
      className={className}
      title={t('view.crusher.export.title')}
      open={isDrawerOpen}
      onClose={onCloseDrawer}
      size={DrawerSize.md}
      testId="export-drawer"
      primaryAction={getPrimaryAction()}
    >
      <ExportContent>
        {isAdmin && (
          <>
            {/* Section 1: Report Type */}
            <SectionContainer>
              <Paragraph size={ParagraphSize.md} weight={ParagraphWeight.bold}>
                {t('view.crusher.export.sectionReportType')}
              </Paragraph>
              <HelperText>
                <Paragraph
                  size={ParagraphSize.sm}
                  color={theme.color.crusherDashboard.detailsModule.lastUpdateTextColor}
                >
                  {equipmentUnit.customerUnitName}
                </Paragraph>
              </HelperText>
              <SectionContent>
                <Select
                  label={t('view.crusher.export.reportType')}
                  value={ExportReportType.mainDashboard}
                  items={[
                    {
                      id: ExportReportType.mainDashboard,
                      text: t('view.crusher.export.mainDashboard'),
                    },
                  ]}
                  placeholder={t('view.crusher.export.mainDashboard')}
                  disabled
                  testId="export-report-type"
                />
              </SectionContent>
            </SectionContainer>

            {/* Section 2: Period Selection */}
            <SectionContainer>
              <Paragraph size={ParagraphSize.md} weight={ParagraphWeight.bold}>
                {t('view.crusher.export.sectionPeriod')}
              </Paragraph>
              <HelperText>
                <Paragraph
                  size={ParagraphSize.sm}
                  color={theme.color.crusherDashboard.detailsModule.lastUpdateTextColor}
                >
                  {t('view.crusher.export.periodHelperText')}
                </Paragraph>
              </HelperText>
              <SectionContent>
                <PeriodSelectorContainer>
                  <DatePickerWrapper>
                    <DatePicker
                      type={DatePickerType.rangePicker}
                      value={
                        state.startDate && state.endDate
                          ? [dayjs(state.startDate), dayjs(state.endDate)]
                          : null
                      }
                      onChange={(value): void => {
                        if (Array.isArray(value) && value[0] && value[1]) {
                          const start: Date = value[0].toDate();
                          const end: Date = value[1].toDate();

                          setState((prev: State): State => ({
                            ...prev,
                            startDate: start,
                            endDate: end,
                            periodPreset: null,
                            currentTask: null,
                          }));
                        }
                      }}
                      disabled={isTaskActive}
                      testId="export-date-picker"
                    />
                  </DatePickerWrapper>

                  <PresetsWrapper>
                    <OptionsSwitch
                      options={periodPresetOptions}
                      value={state.periodPreset ?? ''}
                      onChange={(
                        value: OptionsSwitchProps['value'],
                      ): void => handlePresetSelection(value as ExportPeriodPreset)}
                      disabled={isTaskActive}
                      testId="export-period-presets"
                    />
                  </PresetsWrapper>
                </PeriodSelectorContainer>
              </SectionContent>
            </SectionContainer>

            {/* Section 3: Measurement Units */}
            <SectionContainer>
              <Paragraph size={ParagraphSize.md} weight={ParagraphWeight.bold}>
                {t('view.crusher.export.sectionMeasurementUnits')}
              </Paragraph>
              <SectionContent>
                <Select
                  label={t('common.temperature')}
                  placeholder={t('common.temperature')}
                  items={[
                    { text: 'Celsius', id: TemperatureType.c },
                    { text: 'Fahrenheit', id: TemperatureType.f },
                  ]}
                  value={state.temperatureUnit}
                  onChange={(value): void => setState((prev: State): State => ({
                    ...prev,
                    temperatureUnit: value as TemperatureType,
                    currentTask: null,
                  }))}
                  allowClear={false}
                  disabled={isTaskActive}
                  testId="export-temperature-unit"
                />
                <Select
                  label={t('common.length')}
                  placeholder={t('common.length')}
                  items={[
                    { text: t('common.measurement.millimeter'), id: LengthType.mm },
                    { text: t('common.measurement.inch'), id: LengthType.inch },
                  ]}
                  value={state.lengthUnit}
                  onChange={(value): void => setState((prev: State): State => ({
                    ...prev,
                    lengthUnit: value as LengthType,
                    currentTask: null,
                  }))}
                  allowClear={false}
                  disabled={isTaskActive}
                  testId="export-length-unit"
                />
                <Select
                  label={t('common.pressure')}
                  placeholder={t('common.pressure')}
                  items={[
                    { text: t('common.measurement.megaPascal'), id: PressureType.MPa },
                    { text: PressureType.bar, id: PressureType.bar },
                    { text: PressureType.PSI, id: PressureType.PSI },
                  ]}
                  value={state.pressureUnit}
                  onChange={(value): void => setState((prev: State): State => ({
                    ...prev,
                    pressureUnit: value as PressureType,
                    currentTask: null,
                  }))}
                  allowClear={false}
                  disabled={isTaskActive}
                  testId="export-pressure-unit"
                />
              </SectionContent>
            </SectionContainer>

            {/* Task status display */}
            {state.currentTask && (
              <TaskStatusContainer data-testid="export-task-status">
                {state.currentTask.status === AsyncTaskStatus.pending && (
                  <Loader size={LoaderSize.sm} testId="export-pending-loader" />
                )}

                {state.currentTask.status === AsyncTaskStatus.processing && (
                  <Loader size={LoaderSize.sm} testId="export-processing-loader" />
                )}

                {state.currentTask.status === AsyncTaskStatus.completed
                  && state.currentTask.metadata?.result?.noData && (
                    <Paragraph size={ParagraphSize.sm} color={theme.color.alert.warningText}>
                      {t('view.crusher.export.noData')}
                    </Paragraph>
                )}

                {state.currentTask.status === AsyncTaskStatus.completed
                  && !state.currentTask.metadata?.result?.noData && (
                    <Paragraph size={ParagraphSize.sm} color={theme.color.alert.successText}>
                      {String(t('view.crusher.export.statusCompleted', {
                        total:
                          state.currentTask.metadata?.result?.totalRecords?.toLocaleString()
                          || '0',
                      }))}
                    </Paragraph>
                )}

                {state.currentTask.status === AsyncTaskStatus.failed && (
                  <Alert
                    closable={false}
                    description={t('view.crusher.export.statusFailedDescription')}
                    icon={<AlertLine />}
                    testId="export-error-alert"
                    title={String(t('view.crusher.export.statusFailed'))}
                    type={AlertType.error}
                  />
                )}
              </TaskStatusContainer>
            )}

          </>
        )}
      </ExportContent>
    </Drawer>
  );
}

export { Export };
